<template>
    <div>
        <div class="mb-3 text-right">
            <hts-form-button
                v-if="checkPermission(layoutPermissions.order_add)"
                color="main"
                icon-name="hi-plus-circle-icon"
                icon-position="right"
                type="submit"
                @click="showAdd">
                {{ $t('labels.add') }}
            </hts-form-button>
        </div>

        <hts-data-table
            ref="dataTables"
            :headers="headers"
            :server="apiListOrder"
            use-selection
            :use-archive="useDriverArchive"
            store-name="orderDataTable"
            store-key="Order" />

        <add-order
            v-if="checkPermission(layoutPermissions.order_add)"
            ref="addOrder"
            @refresh-tables="refreshTables()" />

        <edit-order
            v-if="checkPermission(layoutPermissions.order_edit)"
            ref="editOrder"
            @refresh-tables="refreshTables()" />

        <detail-order
            v-if="checkPermission(layoutPermissions.order_management)"
            ref="detailOrder"
            @refresh-tables="refreshTables()" />

        <hts-modal
            v-if="checkPermission(layoutPermissions.order_delete)"
            :show="showPopupDelete"
            :title="$t('labels.orderDelete')"
            type="confirm"
            :content="$t('labels.orderConfirmDelete')"
            @close="closePopupDelete"
            @confirm="() => actionDelete()" />
        <view-history
            ref="viewHistory"
            module="order" />

        <cancel
            v-if="checkPermission(layoutPermissions.order_edit)"
            ref="cancel"
            @refreshTables="refreshTables" />

        <document
            v-if="checkPermission(layoutPermissions.order_document_management)"
            ref="document"
            type="vehicle" />
    </div>
</template>

<script setup lang="ts">
import layoutPermissions from '@/data/permissions/layout-permissions'
import { checkPermission } from '@/utils/permission-check'
import Status from '~/components/status.vue'
import Actions from '~/pages/trucker-[id]/driver/ignore/actions.vue'
import ViewHistory from '../-components/activity.vue'
import AddOrder from './ignore/add-order.vue'
import Cancel from './ignore/cancel.vue'
import CRMStatus from './ignore/component/CRMStatus.vue'
import Deadline from './ignore/component/deadline.vue'
import DeliveryWarehouse from './ignore/component/delivery-warehouse.vue'
import LoadingTask from './ignore/component/loading-task.vue'
import Request from './ignore/component/request.vue'
import DetailOrder from './ignore/detail-order/index.vue'
import Document from './ignore/document/document.vue'
import EditOrder from './ignore/edit-order.vue'
import data from './ignore/imports/data'
import CeilCont from './ignore/table/cont.vue'
import CeilCustomer from './ignore/table/customer.vue'

const { checkShowCancel } = data()

const route = useRoute()
const store = $store()

const action = computed(() => String(route.query.action) || '')
const orderId = computed(() => String(route.query.orderId) || '')
const tab = computed(() => String(route.query.tab) || '')
const cancel = ref()
const apps = computed<any[]>(() => store.system.apps)
onMounted(() => {
  handleOnMountPage()
})

watch(
  () => action.value,
  () => {
    handleOnMountPage()
  }
)
watch(
  () => orderId.value,
  () => {
    handleOnMountPage()
  }
)

const handleOnMountPage = () => {
  if (action.value === 'view' && orderId.value) {
    detailOrder.value.open(orderId.value, tab.value)
  }
}

const { $formatters } = useNuxtApp()

const apiListOrder = $url.order.order['get-list']

const dataTables = ref()
const addOrder = ref()
const editOrder = ref()
const detailOrder = ref()
const showPopupDelete = ref(false)
const selectedForDelete: { [key: string]: any } | null = ref({})
const document = ref()

const showAdd = () => {
  if (addOrder.value) {
    addOrder.value.open()
  }
}

const showEdit = (data: any) => {
  if (editOrder.value) {
    editOrder.value.open(data._id)
  }
}

const viewHistory = ref()
const handleViewHistory = (data: any) => {
  viewHistory.value && viewHistory.value.open(data._id)
}

const handleViewDetail = (data: any) => {
  if (editOrder.value) {
    detailOrder.value.open(data._id)
  }
}

const handleAllotment = (data: any) => {
  if (editOrder.value) {
    detailOrder.value.open(data._id, 'allotment')
  }
}

const refreshTables = () => {
  if (dataTables.value) {
    dataTables.value.refresh()
  }
}

const handleDelete = (data: Object) => {
  selectedForDelete.value = { ...data }
  showPopupDelete.value = true
}

const handleCancel = (data: any) => {
  if (cancel.value) {
    cancel.value.open(data._id)
  }
}

const handleViewDocument = (order: any) => {
  if (!order) {
    return null
  }

  if (document.value) {
    document.value.openDrawerDocument(order._id)
  }
}

onMounted(() => {
  const route = useRoute()
  if (route.query.id) {
    handleViewDetail({ _id: route.query.id })
  }
})

const headers = computed<any[]>(() => {
  const tmp: any[] = [
    {
      field: 'code',
      headerName: 'Mã đơn hàng',
      sortable: true,
      filter: 'agTextColumnFilter',
      valueGetter: (params: any) => {
        return params.data.code || ''
      }
    },
    {
      field: 'noBooking',
      headerName: $t('labels.noBooking'),
      sortable: true,
      filter: 'agTextColumnFilter',
      valueGetter: (params: any) => {
        return params.data.noBooking || ''
      }
    },
    {
      field: 'allocationStatus',
      headerName: $t('labels.allocationStatus'),
      cellRenderer: Status,
      cellRendererParams: {
        module: 'orderAllocationStatus',
        label: '',
        keyStatus: 'allocationStatus'
      },
      filter: 'TableFilterStatus',
      filterItems: [
        { name: $t('labels.all'), value: null },
        {
          name: 'Thiếu thời gian tối ưu',
          value: 2,
          class: { '!bg-amber-100': true, '!text-amber-800': true } // Màu cam đậm
        },
        {
          name: 'Thiếu báo giá',
          value: 1,
          class: { '!bg-violet-100': true, '!text-violet-800': true } // Màu tím
        },
        {
          name: 'Thiếu báo giá & thời gian tối ưu',
          value: 3,
          class: { '!bg-orange-100': true, '!text-orange-800': true } // Màu đỏ cam
        },
        {
          name: 'Chờ phân bổ',
          value: 4,
          class: { '!bg-gray-100': true, '!text-gray-800': true } // Màu xám
        },
        {
          name: 'Phân bổ dở dang',
          value: 5,
          class: { '!bg-info-100': true, '!text-info-800': true } // Màu xanh dương nhạt
        },
        {
          name: 'Hoàn tất phân bổ',
          value: 6,
          class: { '!bg-green-100': true, '!text-green-800': true } // Màu xanh lá cây
        },
        {
          name: $t('labels.cancel'),
          value: -1,
          class: { '!bg-error-100': true, '!text-error-800': true } // Màu đỏ
        }
      ],
      filterModelDefault: { type: 'equals', filterType: 'number' }
    },
    {
      field: 'status',
      headerName: $t('labels.shippingStatus'),
      cellRenderer: Status,
      cellRendererParams: {
        module: 'orderStatus',
        label: '',
        keyStatus: 'status'
      },
      filter: 'TableFilterStatus',
      filterItems: [
        { name: $t('labels.all'), value: null },
        { name: 'Chưa thực hiện', value: 1, class: { '!bg-gray-100': true, '!text-gray-800': true } },
        { name: 'Đang thực hiện', value: 2, class: { '!bg-info-100': true, '!text-info-800': true } },
        { name: 'Hoàn thành', value: 3, class: { '!bg-success-100': true, '!text-success-800': true } },
        { name: $t('labels.cancel'), value: -1, class: { '!bg-error-100': true, '!text-error-800': true } }
      ],
      filterModelDefault: { type: 'equals', filterType: 'number' }
    },
    {
      field: 'customerAbbreviationName',
      headerName: $t('labels.customer'),
      filter: 'agTextColumnFilter',
      autoHeight: true,
      cellRenderer: CeilCustomer
    },
    {
      field: 'numberContainer',
      headerName: 'Loại xe/ loại cont',
      autoHeight: true,
      cellRenderer: CeilCont
    },
    {
      field: 'loadingTask',
      headerName: 'Thời gian đóng hàng',
      autoHeight: true,
      cellRenderer: LoadingTask
    },
    {
      field: 'deadline',
      headerName: $t('labels.deadline'),
      autoHeight: true,
      cellRenderer: Deadline
    },
    {
      field: 'chargeName',
      headerName: $t('labels.route'),
      valueGetter: (params: any) => {
        let chargeName = params.data?.chargeName || ''
        if (chargeName) {
          return chargeName
        }
        chargeName = (params.data?.positions || []).map((p: any) => p?.fullNameNode).join(' >>> ')
        return chargeName
      }
    },
    {
      field: 'deliveryWarehouse',
      headerName: $t('labels.deliveryWarehouse'),
      autoHeight: true,
      filter: 'agTextColumnFilter',
      cellRenderer: DeliveryWarehouse
    },

    {
      field: 'mergeContainer',
      headerName: 'Yêu cầu đặc biệt',
      valueGetter: (params: any) => {
        const results = [] as string[]
        if (params?.data?.mergeContainer) {
          results.push($t('labels.mergeContainer'))
        }

        if (params?.data?.headContainer) {
          results.push($t('labels.headContainer'))
        }

        return results.join(', ')
      }
    },
    {
      field: 'request',
      headerName: $t('labels.note'),
      cellRenderer: Request
    },
    {
      field: 'shipmentCode',
      headerName: 'Số tham chiếu',
      valueGetter: (params: any) => {
        const shipmentCode = params.data.shipmentCode
        return shipmentCode
      }
    },

    {
      field: 'truckOrderCode',
      headerName: 'Truck Order',
      sortable: true
    },
    {
      field: 'container',
      headerName: 'Container',
      valueGetter: (params: any) => {
        const container = params.data.container
        return container?.code
        // return container?.name || ''
      }
    },
    {
      field: 'createdAt',
      headerName: $t('labels.createdAt'),
      sortable: true,
      filter: 'agDateColumnFilter',
      valueGetter: (params: any) => {
        return $formatters.datetime(params.data.createdAt)
      }
    },
    {
      field: 'action',
      pinned: 'right',
      sortable: false,
      lockPosition: true,
      lockPinned: true,
      headerName: $t('labels.action'),
      cellStyle: { overflow: 'visible' },
      cellRenderer: Actions,
      cellRendererParams: {
        storeKey: 'orderDataTable',
        storeName: 'Order',
        items: [
          {
            icon: 'hi-eye-icon',
            label: $t('labels.viewDetail'),
            class: 'text-blue-600 hover:text-blue-500',
            permissions: checkPermission(layoutPermissions.order_management),
            isAll: true,
            click: handleViewDetail
          },
          {
            icon: 'hi-eye-icon',
            label: $t('labels.viewDocument'),
            class: 'text-blue-600 hover:text-blue-500',
            permissions: checkPermission(layoutPermissions.order_document_management),
            click: handleViewDocument
          },
          {
            icon: 'hi-arrows-pointing-out-icon',
            label: $t('labels.allotment'),
            class: 'text-blue-600 hover:text-blue-500',
            permissions: checkPermission(layoutPermissions.order_allotment_add),
            click: handleAllotment
          },
          {
            icon: 'hi-pencil-square-icon',
            label: $t('labels.edit'),
            class: 'text-main-600 hover:text-main-500',
            permissions: checkPermission(layoutPermissions.order_edit),
            click: showEdit
          },
          {
            icon: 'hi-x-circle-icon',
            label: $t('labels.cancel'),
            class: 'text-error-500 hover:text-error-500',
            permissions: checkPermission(layoutPermissions.order_management),
            validate: checkShowCancel,
            isAll: true,
            click: handleCancel
          },
          {
            icon: 'hi-clock-icon',
            label: $t('labels.viewHistory'),
            class: 'text-orange-600 hover:text-orange-500',
            permissions: checkPermission(layoutPermissions.order_activity),
            click: handleViewHistory
          },
          {
            icon: 'hi-trash-icon',
            label: $t('labels.delete'),
            class: 'text-error-500 hover:text-error-500',
            permissions: checkPermission(layoutPermissions.order_delete),
            isArchive: true,
            click: handleDelete
          }
        ]
      }
    }
  ]
  if (apps.value.includes('hwl-crm')) {
    tmp.splice(2, 0, {
      field: 'crm',
      headerName: $t('labels.crmId'),
      sortable: true,
      cellRenderer: CRMStatus
    })
  }
  if (apps.value.includes('freightek')) {
    tmp.splice(2, 0, {
      field: 'freightekTruckOrderCode',
      headerName: $t('labels.freightekId'),
      sortable: true
    })
  }
  return tmp
})

const useDriverArchive = { api: $url.order.order.archive, permissions: checkPermission(layoutPermissions.order_archive) }

const closePopupDelete = () => {
  selectedForDelete.value = {}
  showPopupDelete.value = false
}

const actionDelete = () => {
  const api = $url.order.order.delete

  $api(api, { body: { ids: [selectedForDelete.value?._id || null] } }).then((data: any) => {
    const resData: any = data?.data?.value
    if (resData?.success) {
      closePopupDelete()
      refreshTables()
      process.client && $toast().success(resData.data)
    }
  })
}
</script>
